import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StickyNote, ChevronDown, Pencil, X, Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCollapsibleState } from "@/hooks/useCollapsibleState";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface OrderNotesCardProps {
  notes: string | null;
  orderId: string;
}

export function OrderNotesCard({ notes, orderId }: OrderNotesCardProps) {
  const [isOpen, setIsOpen] = useCollapsibleState("order-notes", true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedNotes, setEditedNotes] = useState(notes || "");
  const queryClient = useQueryClient();

  const updateNotesMutation = useMutation({
    mutationFn: async (newNotes: string) => {
      const { error } = await supabase
        .from("orders")
        .update({ notes: newNotes || null })
        .eq("id", orderId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Muistiinpanot tallennettu");
      queryClient.invalidateQueries({ queryKey: ["order-detail", orderId] });
      setIsEditing(false);
    },
    onError: (error) => {
      console.error("Error updating notes:", error);
      toast.error("Muistiinpanojen tallentaminen epäonnistui");
    },
  });

  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditedNotes(notes || "");
    setIsEditing(true);
    if (!isOpen) setIsOpen(true);
  };

  const handleCancel = () => {
    setEditedNotes(notes || "");
    setIsEditing(false);
  };

  const handleSave = () => {
    updateNotesMutation.mutate(editedNotes.trim());
  };

  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader className="pb-3">
          <CollapsibleTrigger className="flex w-full items-center justify-between hover:opacity-80 transition-opacity">
            <CardTitle className="flex items-center gap-2">
              <StickyNote className="h-5 w-5" />
              Muistiinpanot
            </CardTitle>
            <div className="flex items-center gap-2">
              {!isEditing && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleStartEdit}
                  title="Muokkaa muistiinpanoja"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
              <ChevronDown 
                className={cn(
                  "h-4 w-4 text-muted-foreground transition-transform duration-200",
                  isOpen && "rotate-180"
                )} 
              />
            </div>
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="pt-0">
            {isEditing ? (
              <div className="space-y-3">
                <Textarea
                  value={editedNotes}
                  onChange={(e) => setEditedNotes(e.target.value)}
                  placeholder="Kirjoita muistiinpanoja..."
                  rows={4}
                  className="resize-none"
                  autoFocus
                />
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancel}
                    disabled={updateNotesMutation.isPending}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Peruuta
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={updateNotesMutation.isPending}
                  >
                    {updateNotesMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4 mr-1" />
                    )}
                    Tallenna
                  </Button>
                </div>
              </div>
            ) : notes ? (
              <p className="text-sm whitespace-pre-wrap">{notes}</p>
            ) : (
              <p className="text-sm text-muted-foreground">Ei muistiinpanoja</p>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
